// src/components/admin/VariableInsertPanel.jsx — PR #145
//
// Panneau modal d'insertion de variables Mustache dans les textareas
// du Livret. Déclenché par le bouton "📋 Insérer variable" au-dessus
// de chaque textarea pertinent.
//
// Fonctionnalités :
// - 5 catégories (Voyageur, Réservation, Logement, Codes d'accès,
//   Conciergerie, Computed) collapsibles
// - Recherche par nom + label (case-insensitive)
// - Affiche la valeur actuelle pour la résa de test sélectionnée
// - Click sur une variable : insère {var_name} au curseur du textarea
//   actif et ferme le panneau
// - Filtrage dynamique des codes d'accès : seul box_code apparaît
//   toujours, les autres seulement si log.{code}_code non-vide
//   (PR #143 cohérence avec CodesAccesSection)

import { useState, useMemo, useEffect } from 'react'
import { DS } from '../../lib/ui'
import { listAvailableVariables, resolveAndDescribe } from '../../lib/template-variables'

const OPTIONAL_CODES = ['door_code', 'interphone_code', 'coffre_code', 'garage_code']
const ALWAYS_VISIBLE_CODES = ['box_code']

export default function VariableInsertPanel({ log, ctx, onInsert, onClose }) {
  const [search, setSearch] = useState('')
  const [collapsed, setCollapsed] = useState({}) // { 'cat name': true }

  // Esc ferme le panneau
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  // Filtrage dynamique codes d'accès selon log.*_code (SCOPE C)
  const visibleCodeKeys = useMemo(() => {
    const set = new Set(ALWAYS_VISIBLE_CODES)
    for (const k of OPTIONAL_CODES) {
      const v = log?.[k]
      if (v != null && String(v).trim() !== '') set.add(k)
    }
    return set
  }, [log])

  const catalog = useMemo(() => listAvailableVariables(), [])

  // Filtrage par recherche + visibility codes
  const filteredCatalog = useMemo(() => {
    const q = search.trim().toLowerCase()
    return catalog.map(cat => {
      let vars = cat.variables
      // Filtrage codes : si la catégorie est "Logement" et la var est un
      // *_code, vérifier visibleCodeKeys
      vars = vars.filter(v => {
        if (v.insertAs === 'block') return false // booléen de bloc {#if} : inséré inline il rendrait "true"
        if ([...ALWAYS_VISIBLE_CODES, ...OPTIONAL_CODES].includes(v.key)) {
          if (!visibleCodeKeys.has(v.key)) return false
        }
        if (!q) return true
        return v.key.toLowerCase().includes(q) || (v.label || '').toLowerCase().includes(q)
      })
      return { ...cat, variables: vars }
    }).filter(cat => cat.variables.length > 0)
  }, [catalog, search, visibleCodeKeys])

  function valueFor(key) {
    const r = resolveAndDescribe(key, ctx)
    if (r.value == null || r.value === '') return null
    return String(r.value)
  }

  return (
    <div onClick={onClose}
      style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:1200,display:'flex',alignItems:'center',justifyContent:'center',padding:16,fontFamily:DS.fontBody}}>
      <div onClick={e => e.stopPropagation()}
        style={{background:'#fff',width:'100%',maxWidth:480,maxHeight:'85vh',display:'flex',flexDirection:'column',borderRadius:12,boxShadow:'0 10px 40px rgba(0,0,0,.2)',overflow:'hidden'}}>
        {/* Header */}
        <div style={{padding:'14px 16px',borderBottom:`1px solid ${DS.border}`,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div>
            <div style={{fontSize:11,letterSpacing:'.18em',textTransform:'uppercase',color:'#888',fontWeight:600}}>Insertion</div>
            <div style={{fontFamily:DS.fontDisplay,fontStyle:'italic',fontSize:16,fontWeight:700,color:DS.navy}}>📋 Insérer une variable</div>
          </div>
          <button onClick={onClose} aria-label="Fermer"
            style={{background:'transparent',border:'none',fontSize:24,cursor:'pointer',color:'#888',padding:4,lineHeight:1}}>×</button>
        </div>

        {/* Recherche */}
        <div style={{padding:'10px 16px',borderBottom:`1px solid ${DS.border}`}}>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            autoFocus
            placeholder="Rechercher une variable… (ex: code, prénom, wifi)"
            style={{width:'100%',padding:'8px 12px',borderRadius:8,border:`1px solid ${DS.border}`,fontSize:13,fontFamily:DS.fontBody,background:'#f8fafd',outline:'none'}}/>
        </div>

        {/* Liste catégorisée */}
        <div style={{flex:1,overflowY:'auto',padding:'8px 0'}}>
          {filteredCatalog.length === 0 && (
            <div style={{padding:'20px 16px',textAlign:'center',fontSize:13,color:'#888',fontStyle:'italic'}}>
              Aucune variable trouvée pour "{search}"
            </div>
          )}
          {filteredCatalog.map(cat => {
            const isCollapsed = collapsed[cat.category]
            return (
              <div key={cat.category}>
                <button type="button" onClick={() => setCollapsed(c => ({ ...c, [cat.category]: !c[cat.category] }))}
                  style={{
                    width:'100%',padding:'8px 16px',background:'#f8fafd',border:'none',
                    display:'flex',justifyContent:'space-between',alignItems:'center',
                    fontSize:11,letterSpacing:'.12em',textTransform:'uppercase',fontWeight:700,
                    color:DS.navy,fontFamily:DS.fontBody,cursor:'pointer',
                    borderTop:`1px solid ${DS.border}`,borderBottom:isCollapsed ? `1px solid ${DS.border}` : 'none',
                  }}>
                  <span>{cat.category} ({cat.variables.length})</span>
                  <span style={{fontSize:10,color:'#888'}}>{isCollapsed ? '▶' : '▼'}</span>
                </button>
                {!isCollapsed && cat.variables.map(v => {
                  const value = valueFor(v.key)
                  return (
                    <button key={v.key} type="button" onClick={() => onInsert(v.key)}
                      style={{
                        width:'100%',padding:'10px 16px',background:'transparent',border:'none',
                        textAlign:'left',cursor:'pointer',fontFamily:DS.fontBody,
                        display:'grid',gridTemplateColumns:'1fr auto',gap:8,alignItems:'baseline',
                        borderBottom:`1px solid ${DS.border}aa`,
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#eaf3fb'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <div>
                        <div style={{fontFamily:'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',fontSize:12,color:DS.navy,fontWeight:700}}>
                          {`{${v.key}}`}
                        </div>
                        {v.label && (
                          <div style={{fontSize:11,color:'#666',marginTop:2,lineHeight:1.4}}>{v.label}</div>
                        )}
                      </div>
                      <div style={{fontSize:11,fontStyle:'italic',color: value ? '#666' : '#bbb',whiteSpace:'nowrap',maxWidth:140,overflow:'hidden',textOverflow:'ellipsis'}}
                        title={value || ''}>
                        {value ? (value.length > 30 ? value.slice(0, 30) + '…' : value) : '—'}
                      </div>
                    </button>
                  )
                })}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
